import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTranslate } from '@tolgee/react';
import { AwsPdfResponse } from '../AwsGraphs';
import { formatNumber } from 'utils/formatNumber';

/**
 * Props for SavingsBreakdownPie component.
 */
export interface SavingsBreakdownPieProps {
    data: AwsPdfResponse;
}

/**
 * Renders a pie chart showing the breakdown of possible savings.
 * @param {SavingsBreakdownPieProps} props - The props for the component.
 */
const SavingsBreakdownPie: React.FC<SavingsBreakdownPieProps> = ({ data }) => {
    const { t } = useTranslate();
    const top3savingsEntries = Object.entries(data.possible_savings_flat || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
    const top3savingsLabels = top3savingsEntries.map(([key]) => key);
    const top3savingsValues = top3savingsEntries.map(([, value]) => value);
    const options: ApexOptions = {
        chart: {
            type: 'pie',
            height: 400,
            fontFamily: 'inherit'
        },
        colors: ['#ef11e9', '#fad0fe', '#0003c9'],
        labels: top3savingsLabels.map((label) => label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' ')),
        legend: {
            position: 'bottom',
            fontSize: '14px',
            offsetY: 0,
            itemMargin: {
                horizontal: 8,
                vertical: 5
            }
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: (val) => `$${formatNumber(val)}`
            }
        },
        plotOptions: {
            pie: {
                offsetY: 0,
                customScale: 0.9,
                donut: {
                    size: '65%'
                }
            }
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: '12px',
                fontWeight: 400
            },
            formatter: function (val: number, opts: any) {
                // Get the actual value from the series data
                const value = opts.w.config.series[opts.seriesIndex];
                return '$' + value;
            },
            dropShadow: {
                enabled: false
            }
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        width: 200
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }
        ]
    };
    return (
        <Box sx={{ minWidth: 0 }}>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2.5px solid #000000',
                    filter: 'drop-shadow(8px 8px 0 black)',
                    borderRadius: '.5rem'
                }}
            >
                <CardContent
                    sx={{
                        pt: 2,
                        pb: 3,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: 18 }}>
                            {t('pdf_success.charts.savings_breakdown.title')}
                        </Typography>
                        <Typography variant="h5">{t('pdf_success.charts.savings_breakdown.subtitle')}</Typography>
                    </Box>
                    <Chart options={options} series={top3savingsValues as number[]} type="pie" height={350} />
                </CardContent>
            </Card>
        </Box>
    );
};

export default SavingsBreakdownPie;
